import React from 'react';

/**
 * FiveLayerTrace — visualization of the platform's five-layer
 * enforcement stack for a single execution.
 *
 * Per Design Principles §7.1 (the platform's biggest differentiator):
 * every governance verdict displays which of the five layers fired and
 * what the verdict was at each layer. The five layers, in order:
 *
 *   L1 · Policy Rules        (deterministic, ~5ms)
 *   L2 · DPG                 (data-privacy-gateway scan)
 *   L3 · Autonomy Gate       (trust-based gating)
 *   L4 · Inference Routing   (self vs platform inference)
 *   L5 · Audit               (hash-chained logging)
 *
 * Each layer's verdict is one of: 'allow' | 'warn' | 'block' | 'skipped'
 * | 'logged' (L5 default). Click any layer to drill into the
 * underlying decision data via the optional `onLayerClick` handler.
 *
 * @param {Object} props
 * @param {Array<{
 *   id?: string,
 *   layer: 1|2|3|4|5,
 *   name?: string,
 *   verdict: 'allow'|'warn'|'block'|'skipped'|'logged',
 *   detail?: React.ReactNode,
 * }>} props.layers
 *   Pass exactly 5 entries (one per layer). Order is enforced by `layer`
 *   field, so the array can be in any order.
 * @param {(layer: any) => void} [props.onLayerClick]
 */
export function FiveLayerTrace({ layers, onLayerClick }) {
  /* Fix G9: "Autonomy Gate" wraps to two lines in narrow containers
   * (slideout @520px), making the verdict pill render below the wrapped
   * second line — visually it looks like the verdict label was rendered
   * twice. Shorten to "Autonomy" since the layer index "L3" is shown
   * above and the aria-label keeps the full long name for screen readers. */
  const DEFAULT_NAMES = {
    1: 'Policy',
    2: 'DPG',
    3: 'Autonomy',
    4: 'Inference',
    5: 'Audit',
  };

  const VERDICT_TONE = {
    allow:   { bg: 'bg-v2-success-soft', text: 'text-v2-success', border: 'border-v2-success' },
    warn:    { bg: 'bg-v2-warning-soft', text: 'text-v2-warning', border: 'border-v2-warning' },
    block:   { bg: 'bg-v2-danger-soft',  text: 'text-v2-danger',  border: 'border-v2-danger' },
    skipped: { bg: 'bg-v2-muted',        text: 'text-v2-ink-muted', border: 'border-v2-border' },
    logged:  { bg: 'bg-v2-action-soft',  text: 'text-v2-action',  border: 'border-v2-action' },
  };

  const VERDICT_LABEL = {
    allow:   'ALLOW',
    warn:    'WARN',
    block:   'BLOCK',
    skipped: 'skipped',
    logged:  'LOGGED',
  };

  /* Long, canonical names used in aria-label so screen readers always
   * hear the full layer name even though the visible label is short
   * to avoid wrap-induced visual artifacts in narrow containers. */
  const FULL_NAMES = {
    1: 'Policy Rules',
    2: 'Data Privacy Gateway',
    3: 'Autonomy Gate',
    4: 'Inference Routing',
    5: 'Audit Logging',
  };

  const ordered = [1, 2, 3, 4, 5].map((n) => layers.find((l) => l.layer === n) || { layer: n, verdict: 'skipped' });

  return (
    <div
      role="group"
      aria-label="Five-layer verdict trace"
      className="bg-v2-muted border border-v2-border rounded-v2-sm p-2 flex items-stretch gap-1 overflow-x-auto"
    >
      {ordered.map((l) => {
        const tone = VERDICT_TONE[l.verdict] || VERDICT_TONE.skipped;
        const name = l.name || DEFAULT_NAMES[l.layer];
        const fullName = l.name || FULL_NAMES[l.layer] || name;
        const isInteractive = typeof onLayerClick === 'function';
        const Wrapper = isInteractive ? 'button' : 'div';

        return (
          <Wrapper
            key={l.layer}
            type={isInteractive ? 'button' : undefined}
            onClick={isInteractive ? () => onLayerClick(l) : undefined}
            aria-label={`Layer ${l.layer} ${fullName}: ${VERDICT_LABEL[l.verdict] || l.verdict}`}
            className={
              'v2-focus-ring flex-1 min-w-[88px] text-center rounded-v2-sm py-2 px-2 ' +
              'transition-colors duration-v2 ease-v2 ' +
              tone.bg + ' ' + tone.text + ' ' +
              (isInteractive ? 'cursor-pointer hover:opacity-90' : 'cursor-default')
            }
          >
            <div className="text-[9.5px] font-bold tracking-wider uppercase opacity-70">
              L{l.layer}
            </div>
            <div className="text-[11.5px] font-semibold mt-0.5 truncate" title={name}>{name}</div>
            <div className="text-[10px] mt-0.5 opacity-90 whitespace-nowrap">
              {VERDICT_LABEL[l.verdict] || l.verdict}
              {l.detail && <span className="block">{l.detail}</span>}
            </div>
          </Wrapper>
        );
      })}
    </div>
  );
}

export default FiveLayerTrace;
